import {
  Button,
  ButtonGroup,
  DateRangePicker,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type RangeValue,
  type SharedSelection,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from "@heroui/react";
import {
  type CalendarDate,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import { useStore } from "@nanostores/react";
import { useLocale } from "@react-aria/i18n";
import type { MutationStatus } from "@tanstack/react-query";
import { ChevronDownIcon, ChevronsRightIcon, FilterIcon, SearchIcon, XIcon } from "lucide-react";
import { atom } from "nanostores";
import { useMemo, useRef, useState } from "react";
import { type Address, erc20Abi, formatUnits, parseUnits } from "viem";
import { useReadContract } from "wagmi";
import AztecSymbol from "../../assets/aztec-symbol.svg?react";
import { usePaginatedItems } from "../../hooks/usePaginatedItems";
import { type TxHistoryLog, useTxHistoryQuery } from "../../queries/tx_history";
import type { BridgeClaimParams } from "../../state/types";
import { formatTimestamp } from "../../utils";
import { BRIDGE_LABELS, type BridgeType } from "../bridge/bridgeConfig";
import { ManualClaimModal } from "../manual_claim/ManualClaimModal";
import { BottomContent } from "../table/BottomContent";
import { TxDetailsModal } from "./TxDetailsModal";

const $searchValueAtom = atom("");
const $dateRangeFilterAtom = atom<RangeValue<CalendarDate> | null>(null);
const $currentPageAtom = atom(1);

function setDateRangeFilter(status: RangeValue<CalendarDate> | null) {
  $dateRangeFilterAtom.set(status);
  $currentPageAtom.set(1);
}

function setSearchValue(value: string) {
  $searchValueAtom.set(value);
  $currentPageAtom.set(1);
}

function setCurrentPage(page: number) {
  $currentPageAtom.set(page);
}
export function TxHistoryTable({
  claimMutationStatus,
  onClaim,
}: {
  claimMutationStatus: MutationStatus;
  onClaim: (bridgeClaimParams: BridgeClaimParams) => void;
}) {
  const searchValue = useStore($searchValueAtom);
  const dateRangeFilter = useStore($dateRangeFilterAtom);
  const currentPage = useStore($currentPageAtom);

  const txDataRef = useRef<TxHistoryLog | null>(null);
  const txDetailsModalDisclosure = useDisclosure({
    onClose: () => {
      txDataRef.current = null;
    },
  });

  const [itemsPerPage, setItemsPerPage] = useState(10);

  const tableQuery = useTxHistoryQuery();

  const allTableItems = tableQuery.data ?? [];

  const filteredItems = useMemo(() => {
    let items = [...allTableItems];

    if (searchValue.length > 0) {
      const searchString = searchValue.toLowerCase();
      items = items.filter((item) => {
        if (item.source === "bridge") {
          if (item.isManualClaim) {
            return item.tokenAddress.toLowerCase().includes(searchString);
          }
          return (
            item.txHash.toLowerCase().includes(searchString) ||
            item.tokenAddress.toLowerCase().includes(searchString) ||
            item.tokenSymbol.toLowerCase().includes(searchString) ||
            item.senderAddress.toLowerCase().includes(searchString) ||
            item.recipientAddress.toLowerCase().includes(searchString)
          );
        }
        if (item.source === "bridgeFailed") {
          return false;
        }
        if (item.source === "shielding" && item.txStatus === "failed") {
          return false;
        }
        if (item.source === "transfer" && item.txStatus === "failed") {
          return false;
        }
        return (
          item.txHash.toLowerCase().includes(searchString) ||
          item.tokenAddress.toString().toLowerCase().includes(searchString) ||
          item.tokenSymbol.toLowerCase().includes(searchString) ||
          item.l2AccountAddress.toString().toLowerCase().includes(searchString)
        );
      });
    }

    if (dateRangeFilter !== null) {
      items = items.filter((item) => {
        const date = new Date(item.timestamp);
        return (
          date >= dateRangeFilter.start.toDate(getLocalTimeZone()) &&
          date <= dateRangeFilter.end.toDate(getLocalTimeZone())
        );
      });
    }

    return items;
  }, [allTableItems, dateRangeFilter, searchValue]);

  const currentPageItems = usePaginatedItems(filteredItems, currentPage, itemsPerPage);

  return (
    <>
      <Table
        aria-label="Transaction History"
        topContent={<TopContent />}
        bottomContent={
          <BottomContent
            totalItemSize={filteredItems.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
          />
        }
        selectionMode="single"
        onSelectionChange={(keys: SharedSelection) => {
          if (!keys.anchorKey) return;
          const [source, txHashOrId] = keys.anchorKey.split(":");

          const selectedItem = currentPageItems.find((item) => {
            if (item.source === "bridgeFailed") {
              return item.source === source && item.id === txHashOrId;
            }
            if (item.source === "shielding" && item.txStatus === "failed") {
              return item.source === source && item.id === txHashOrId;
            }
            if (item.source === "transfer" && item.txStatus === "failed") {
              return item.source === source && item.id === txHashOrId;
            }
            return item.source === source && item.txHash === txHashOrId;
          });

          if (!selectedItem) {
            txDataRef.current = null;
            return;
          }

          txDataRef.current = selectedItem;
          txDetailsModalDisclosure.onOpen();
        }}
        classNames={{
          wrapper: "turnstile-card-border",
          th: "text-white hover:!text-white text-sm",
          tbody: "tabular-nums",
        }}
      >
        <TableHeader>
          <TableColumn>Timestamp</TableColumn>
          <TableColumn>Transaction</TableColumn>
          <TableColumn>Network</TableColumn>
          <TableColumn align="end">Amount</TableColumn>
          <TableColumn align="center">Status</TableColumn>
        </TableHeader>
        <TableBody
          items={currentPageItems}
          emptyContent="No events to display."
          isLoading={tableQuery.status === "pending"}
          loadingContent={<Spinner label="Loading..." />}
        >
          {(log) =>
            log.source === "bridge" ? (
              <TableRow key={`${log.source}:${log.txHash}`}>
                <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                <TableCell className="font-semibold">Bridge</TableCell>
                <TableCell>
                  <NetworkCell bridgeType={log.bridgeType} />
                </TableCell>
                <TableCell className="font-semibold">
                  {log.isManualClaim ? "?" : <EthBalance l1TokenAddress={log.tokenAddress} amount={log.amount} />}
                </TableCell>
                <TableCell>
                  {log.state === "claimed" ? (
                    <div className="text-[#36CC36]">Claimed</div>
                  ) : (
                    <Button
                      variant="flat"
                      size="sm"
                      radius="sm"
                      className="bg-[#36CC36]/20 text-[#36CC36] h-auto py-1"
                      isLoading={claimMutationStatus === "pending"}
                      onPress={() => {
                        onClaim({
                          ...log,
                          isManualClaim: false,
                        } as const);
                      }}
                    >
                      Claim
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : log.source === "bridgeFailed" ? (
              <TableRow key={`${log.source}:${log.id}`}>
                <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                <TableCell className="font-semibold">Bridge</TableCell>
                <TableCell>
                  <NetworkCell bridgeType={log.bridgeType} />
                </TableCell>
                <TableCell className="font-semibold">
                  {log.state === "unclaimed" ? (
                    <EthBalance
                      l1TokenAddress={log.bridgeFormData.sourceToken}
                      amount={parseUnits(log.bridgeFormData.transferAmount, log.bridgeFormData.tokenDecimals)}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-red-500">Failed</div>
                </TableCell>
              </TableRow>
            ) : log.source === "shielding" ? (
              <TableRow key={`${log.source}:${log.txStatus === "success" ? log.txHash : log.id}`}>
                <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                <TableCell className="font-semibold">
                  {log.txStatus === "success"
                    ? log.shieldingMode === "shield"
                      ? "Shielding"
                      : "Unshielding"
                    : log.shieldingParams.shieldingMode === "shield"
                      ? "Shielding"
                      : "Unshielding"}
                </TableCell>
                <TableCell>
                  <AztecSymbol className="bg-[#33546C] rounded-full size-8 p-2" />
                </TableCell>
                <TableCell className="font-semibold">
                  {log.txStatus === "success" ? (
                    <>
                      {log.displayAmount} <span className="text-[#89A4B7]">{log.tokenSymbol}</span>
                    </>
                  ) : (
                    <>
                      {log.shieldingParams.displayAmount}{" "}
                      <span className="text-[#89A4B7]">{log.shieldingParams.tokenSymbol}</span>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {log.txStatus === "success" ? (
                    <div className="text-[#36CC36]">Completed</div>
                  ) : (
                    <div className="text-[#FF4D4D]">Failed</div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={`${log.source}:${log.txStatus === "success" ? log.txHash : log.id}`}>
                <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                <TableCell className="font-semibold">Transfer</TableCell>
                <TableCell>
                  <AztecSymbol className="bg-[#33546C] rounded-full size-8 p-2" />
                </TableCell>
                <TableCell className="font-semibold">
                  {log.txStatus === "success" ? (
                    <>
                      {log.displayAmount} <span className="text-[#89A4B7]">{log.tokenSymbol}</span>
                    </>
                  ) : (
                    <>
                      {log.transferParams.displayAmount}{" "}
                      <span className="text-[#89A4B7]">{log.transferParams.tokenSymbol}</span>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {log.txStatus === "success" ? (
                    <div className="text-[#36CC36]">Completed</div>
                  ) : (
                    <div className="text-[#FF4D4D]">Failed</div>
                  )}
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
      <TxDetailsModal
        isOpen={txDetailsModalDisclosure.isOpen}
        onClose={txDetailsModalDisclosure.onClose}
        txData={txDataRef.current}
      />
    </>
  );
}

function NetworkCell({ bridgeType }: { bridgeType: BridgeType }) {
  const TransferFromIcon = BRIDGE_LABELS[bridgeType].transferFromIcon;
  const TransferToIcon = BRIDGE_LABELS[bridgeType].transferToIcon;

  return (
    <div className="flex items-center gap-1">
      <TransferFromIcon className="bg-[#33546C] rounded-full size-8 p-2" />
      <ChevronsRightIcon />
      <TransferToIcon className="bg-[#33546C] rounded-full size-8 p-2" />
    </div>
  );
}

function TopContent() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const searchValue = useStore($searchValueAtom);
  const dateRangeFilter = useStore($dateRangeFilterAtom);

  const { locale } = useLocale();
  const now = today(getLocalTimeZone());
  const thisWeek = {
    start: startOfWeek(now, locale),
    end: endOfWeek(now, locale),
  };
  const lastWeek = {
    start: startOfWeek(now.subtract({ weeks: 1 }), locale),
    end: endOfWeek(now.subtract({ weeks: 1 }), locale),
  };
  const lastMonth = {
    start: startOfMonth(now.subtract({ months: 1 })),
    end: endOfMonth(now.subtract({ months: 1 })),
  };

  return (
    <div className="flex gap-2">
      <div>
        <Input
          variant="flat"
          radius="full"
          isClearable
          placeholder="Search..."
          startContent={<SearchIcon />}
          value={searchValue}
          onClear={() => setSearchValue("")}
          onValueChange={setSearchValue}
          classNames={{
            inputWrapper: "group-data-[hover=true]:bg-[var(--turnstile-input-hover-color)]",
          }}
        />
      </div>
      <Popover placement="bottom" classNames={{ content: "turnstile-modal-border" }}>
        <PopoverTrigger>
          <Button
            variant="bordered"
            color="primary"
            radius="full"
            startContent={<FilterIcon size={16} />}
            endContent={<ChevronDownIcon size={16} />}
            className="text-white"
          >
            Filter
          </Button>
        </PopoverTrigger>
        <PopoverContent className="gap-4 p-4">
          <DateRangePicker
            label="Filter by date"
            labelPlacement="outside"
            calendarProps={{
              focusedValue: dateRangeFilter?.start,
            }}
            value={dateRangeFilter}
            onChange={setDateRangeFilter}
            classNames={{
              inputWrapper: "bg-default-200 group-data-[focus=true]:bg-default-200 focus-within:hover:bg-default-200",
              calendar: "bg-content1",
            }}
          />
          <ButtonGroup fullWidth radius="full" variant="bordered" size="sm">
            <Button onPress={() => setDateRangeFilter(thisWeek)}>This week</Button>
            <Button onPress={() => setDateRangeFilter(lastWeek)}>Last week</Button>
            <Button onPress={() => setDateRangeFilter(lastMonth)}>Last month</Button>
          </ButtonGroup>
          <Button variant="light" size="sm" onPress={() => setDateRangeFilter(null)} startContent={<XIcon size={16} />}>
            Clear
          </Button>
        </PopoverContent>
      </Popover>
      <div className="flex-1" />

      <Button variant="bordered" color="primary" radius="full" onPress={onOpen} className="text-white">
        Manual Claim
      </Button>
      <ManualClaimModal isOpen={isOpen} onClose={onClose} />
    </div>
  );
}

function EthBalance({ l1TokenAddress, amount }: { l1TokenAddress: Address; amount: bigint }) {
  const symbolQuery = useReadContract({
    address: l1TokenAddress,
    abi: erc20Abi,
    functionName: "symbol",
  });

  const decimalsQuery = useReadContract({
    address: l1TokenAddress,
    abi: erc20Abi,
    functionName: "decimals",
  });

  if (!symbolQuery.data || !decimalsQuery.data) {
    return null;
  }

  const amountStr = formatUnits(amount, decimalsQuery.data);

  return (
    <>
      {amountStr} <span className="text-[#89A4B7]">{symbolQuery.data}</span>
    </>
  );
}
